import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { getSupabaseBrowserClient } from "../integrations/supabase/client";
import { sourcedKey, useReadSource, viewRows } from "./readSource";

/**
 * The management relationship as the client's own screen reads it: plan.md section 5.12.
 *
 * The loyalty discount stopped being read here on 2026-09-22, because the company stopped selling
 * it on 2026-09-08. `public.v_loyalty_status` remains until a later round decides its fate.
 *
 * **One view and no gateway since 2026-10-07.** The client ended management here, withdrew the
 * cancellation and answered the attachments question through four gateways until Mattej decided
 * that a client who wants to end writes to Petrovic by mail or in the chat, and Petrovic records it
 * (`app/data/adminLifecycleQueries.ts`, `useAdminRecordClientCancellation`). The rejected alternative
 * is in plan.md section 5.12: a self-service button the client could press by mistake or without
 * talking to Petrovic. The four gateways were dropped by `20261007100001`, so the screen reads the
 * state and nothing else.
 *
 * `public.v_current_management` is `security_invoker`, so the caller's own RLS decides which row
 * comes back, and it filters out a fully `ended` period. A tenant with no row is therefore a real
 * state and not a failed read, which is why the hook below keeps the two apart.
 */

export const currentManagementKey = (tenantId: string) =>
  ["portal", "current-management", tenantId] as const;

/** The six values the check constraint on `management_periods.status` allows. */
export type ManagementStatus =
  | "pending_activation"
  | "active"
  | "paused"
  | "ending"
  | "read_only"
  | "ended";

/** `not_yet_given` is a value and never a null. Migration 3 made the column `not null`. */
export type ProcessorInstructionState = "not_yet_given" | "return" | "delete";

export interface CurrentManagement {
  tenant_id: string;
  management_period_id: string;
  status: string;
  started_on: string | null;
  scheduled_end_on: string | null;
  actual_end_on: string | null;
  read_only_until: string | null;
  end_initiator: string | null;
  end_reason: string | null;
  processor_end_instruction: string;
  portal_access_state: string;
  /**
   * Build round A1, 2026-10-08: the tier today in Europe/Stockholm, `forvaltning` or `utveckling`,
   * computed by the view from `public.management_period_tiers`. Null only for a period that has no
   * tier on or before today, which the migration's backfill leaves none of.
   */
  tier: string | null;
}

/**
 * The client's words for the tier, in full. Admin says "Utveckling" for short
 * (`app/lib/adminLabels.ts` `MANAGEMENT_TIER`); the client reads the name the product is sold under.
 */
export const CLIENT_TIER_LABEL: Record<string, string | undefined> = {
  forvaltning: "Förvaltning",
  utveckling: "Förvaltning och utveckling"
};

/**
 * The current period, or null when there is none.
 *
 * `maybeSingle` answers `{ data: null, error: null }` for zero rows, which is the state of a tenant
 * whose management has fully ended or never started. The screen renders that absence as an absence.
 */
export function useCurrentManagement(
  tenantId: string | null
): UseQueryResult<CurrentManagement | null> {
  const source = useReadSource();
  return useQuery({
    queryKey: sourcedKey(source, currentManagementKey(tenantId ?? "none")),
    enabled: Boolean(tenantId),
    queryFn: async () => {
      if (source.kind === "admin") {
        const rows = await viewRows<CurrentManagement>("admin_as_client_management", { p_tenant_id: source.tenantId });
        return rows[0] ?? null;
      }
      const supabase = getSupabaseBrowserClient();
      const { data, error } = await supabase
        .from("v_current_management")
        .select(
          "tenant_id, management_period_id, status, started_on, scheduled_end_on, actual_end_on, read_only_until, end_initiator, end_reason, processor_end_instruction, portal_access_state, tier"
        )
        .eq("tenant_id", tenantId as string)
        .maybeSingle();

      if (error) {
        throw error;
      }
      return (data as CurrentManagement | null) ?? null;
    }
  });
}
